import { useFieldArray, useFormContext } from 'react-hook-form';
import { TourOptionFormData } from '@/features/dashboard/tourOptions/schemas/tourOptionSchema.ts';
import React, { useEffect } from 'react';
import Option from '@/components/form/types/Option.ts';
import {
  ParticipantLimitMode,
  participantLimitModesForVariant,
} from '@/features/dashboard/tourOptions/types/ParticipantLimitMode.ts';
import TwoInputsRow from '@/components/form/ui/TwoInputsRow.tsx';
import { TextInput } from '@/components/form/TextInput.tsx';
import { ValueInput } from '@/components/form/ValueInput.tsx';
import DangerIconButton from '@/components/buttons/DangerIconButton.tsx';
import { Trash } from 'lucide-react';
import CheckboxInput from '@/components/form/CheckboxInput.tsx';
import ToggleInput from '@/components/form/ToggleInput.tsx';
import AgeRestrictionPriceForm from '@/features/dashboard/tourOptions/components/AgeRestrictionPriceForm.tsx';
import { TourOptionUsageDto } from '@/features/dashboard/tourOptions/types/TourOptionUsageDto.ts';

const TourVariantForm = ({
  index,
  remove,
  currency,
  usage,
  usedPriceIds,
}: {
  index: number;
  remove: (index) => void;
  currency?: string;
  usage?: TourOptionUsageDto;
  usedPriceIds?: Set<string>;
}) => {
  const {
    control,
    register,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<TourOptionFormData>();

  const variants = watch('variants');
  const participationLimitModeValue = watch(
    `variants.${index}.participantLimitMode`,
  );
  const isPriceByAgeValue = watch(`variants.${index}.isPriceByAge`);

  const variantId = watch(`variants.${index}.id`);
  const variantUsage = usage?.variants.find((v) => v.variantId === variantId);
  const isVariantUsed = variantUsage?.used === true;

  const [limitModes, setLimitModes] = React.useState<Option[]>(
    participantLimitModesForVariant,
  );

  useEffect(() => {
    if (participationLimitModeValue === ParticipantLimitMode.NONE) {
      setValue(`variants.${index}.participantLimit`, undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participationLimitModeValue]);

  useEffect(() => {
    if (isPriceByAgeValue) {
      setLimitModes(participantLimitModesForVariant);
      if (ageRestrictionPricesFieldArray.fields.length === 0) {
        ageRestrictionPricesFieldArray.append({ price: 0 });
      }
    } else {
      setLimitModes(participantLimitModesForVariant);
      setValue(`variants.${index}.ageRestrictionPrices`, []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, isPriceByAgeValue]);

  const ageRestrictionPricesFieldArray = useFieldArray({
    control,
    name: `variants.${index}.ageRestrictionPrices`,
  });

  return (
    <div className="overflow-hidden rounded-md border border-neutral-200">
      <div className="h-1 w-full bg-accent-600" />
      <div className="bg-accent-100 p-3">
        <div className="flex flex-row items-end gap-2">
          {isPriceByAgeValue === false ? (
            <TwoInputsRow ratio="2/1" className="gap-2">
              <TextInput
                label="Nazwa wariantu"
                register={register(`variants.${index}.name`)}
                placeholder="Wpisz nazwę wariantu"
                required={true}
                error={errors.variants?.[index]?.name?.message}
              />
              <ValueInput
                label="Cena wariantu"
                register={register(`variants.${index}.price`, {
                  valueAsNumber: true,
                })}
                placeholder="0"
                type="number"
                step={0.01}
                error={errors.variants?.[index]?.price?.message}
                sufix={currency}
              />
            </TwoInputsRow>
          ) : (
            <TextInput
              label="Nazwa wariantu"
              register={register(`variants.${index}.name`)}
              placeholder="Wpisz nazwę wariantu"
              required={true}
              error={errors.variants?.[index]?.name?.message}
            />
          )}

          {variants.length > 2 && (
            <DangerIconButton
              onClick={() => remove(index)}
              disabled={isVariantUsed}
              title={
                isVariantUsed
                  ? 'Nie można usunąć — wariant jest używany w rezerwacjach. Zamiast tego oznacz go jako wycofany z oferty.'
                  : 'Usuń wariant'
              }
            >
              <Trash />
            </DangerIconButton>
          )}
        </div>
      </div>
      <div className="space-y-4 bg-basic-white p-3">
        {variantId && (
          <CheckboxInput
            label="Wycofany z oferty (archiwum)"
            hint="Wariant zniknie z formularza rezerwacji, ale pozostanie widoczny w istniejących rezerwacjach."
            register={register(`variants.${index}.archived`)}
            error={errors.variants?.[index]?.archived?.message}
          />
        )}
        <TwoInputsRow ratio="1/1">
          <CheckboxInput
            label="Cena zależna od wieku"
            register={register(`variants.${index}.isPriceByAge`)}
            error={errors.variants?.[index]?.isPriceByAge?.message}
          />
          {isPriceByAgeValue && (
            <CheckboxInput
              label="Dostępne tylko dla wybranych grup wiekowych"
              register={register(
                `variants.${index}.isVariantOnlyForDefinedAgeRanges`,
              )}
              error={
                errors.variants?.[index]?.isVariantOnlyForDefinedAgeRanges
                  ?.message
              }
            />
          )}
        </TwoInputsRow>
        <TwoInputsRow ratio="3/1">
          <ToggleInput
            fieldPath={`variants.${index}.participantLimitMode`}
            label="Limit rezerwacji"
            required={true}
            options={limitModes}
            control={control}
            error={errors.variants?.[index]?.participantLimitMode?.message}
          />
          <ValueInput
            label="Liczba miejsc"
            register={register(`variants.${index}.participantLimit`, {
              valueAsNumber: true,
            })}
            placeholder="0"
            sufix="osób"
            disabled={
              !(
                participationLimitModeValue === ParticipantLimitMode.PER_VARIANT
              )
            }
            error={errors.variants?.[index]?.participantLimit?.message}
          />
        </TwoInputsRow>
      </div>
      {isPriceByAgeValue && (
        <AgeRestrictionPriceForm
          currency={currency}
          fieldArray={ageRestrictionPricesFieldArray}
          parentFieldName={`variants.${index}`}
          limitsEnabled={false}
          usedPriceIds={usedPriceIds}
        />
      )}
    </div>
  );
};

export default TourVariantForm;
